import { useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	AiStreamingOrchestratorPlugin,
	StreamingError,
} from './aiStreamingOrchestratorPluginType';
import { createStreamFlowPacer } from './editor-actions/stream-flow-pacer';
import { StreamCommandQueue } from './editor-actions/streamCommandQueue';

export const aiStreamingOrchestratorPlugin: AiStreamingOrchestratorPlugin = ({ api, config }) => {
	if (!api) {
		throw new Error('api is not defined');
	}

	let streamQueue: StreamCommandQueue | undefined;

	// Only allow one session to be active at a time
	let sessionId: string | null = null;
	const streamFlowPacer = createStreamFlowPacer(config?.streamFlowPacerEnabled);

	return {
		name: 'aiStreamingOrchestrator',
		actions: {
			startSession: () => {
				sessionId = crypto.randomUUID();
				streamQueue = new StreamCommandQueue(api, streamFlowPacer);
				streamQueue.startSession(sessionId);
				api.aiExperience.actions.startSession(sessionId);
				return sessionId;
			},

			endSession: async (error?: StreamingError) => {
				if (sessionId === null) {
					throw new Error('endSession requires a session to be started first!');
				}

				streamQueue?.queueEndSession(sessionId);
				if (error) {
					const { send } = api.aiExperience.actions.getAiExperienceActor();
					send({
						type: 'error',
						errorInfo: {
							failureReason: error.failureReason,
							statusCode: error.statusCode,
						},
					});
				}
				// Since `endSession` is going to be called after all the actions are invoked
				// so we can basically wait for the queue to be empty
				await streamQueue?.onEmptyQueue();
				streamQueue?.endSession(sessionId);
				// https://atlassian.slack.com/archives/C0962DYFLRL/p1759397599541949?thread_ts=1759366160.706849&cid=C0962DYFLRL
				// streamQueue?.destroy();
				api.aiExperience.actions.endSession(sessionId);
				sessionId = null;
			},

			abortSession: () => {
				// abortSession can be called multiple times from multiple command handlers
				if (!sessionId && fg('platform_editor_ai_aifc_patch_beta_2')) {
					return;
				}

				const sessionToEnd = sessionId;
				sessionId = null;
				streamQueue?.abort();
				if (sessionToEnd) {
					api.aiExperience.actions.endSession(sessionToEnd);
				}
			},

			initAction: (command, options) => {
				if (sessionId === null) {
					throw new Error('initAction requires a session to be started first!');
				}
				const actionId = crypto.randomUUID();
				streamQueue?.initStreamCommand(actionId, command, options);
				return actionId;
			},

			// We are sending the full document as a chunk for create actions
			streamValue: (actionId: string, chunk: string) => {
				// Use the queue manager to handle streaming
				// This will either process immediately if actor is ready, or queue for later
				streamQueue?.queueStreamCommand(actionId, chunk);
			},

			finishAction: (actionId: string, error?: StreamingError) => {
				const { send } = api.aiExperience.actions.getAiExperienceActor();
				if (error) {
					send({
						type: 'error',
						errorInfo: {
							failureReason: error.failureReason,
							statusCode: error.statusCode,
						},
					});
				}
				streamQueue?.endStreamCommand(actionId);
			},
		},
		usePluginHook: () => {
			useEffect(() => {
				return () => {
					streamQueue?.destroy();
				};
			}, []);
		},
	};
};
